import api from './api';

export interface LoginCredentials {
  /** Email and/or phone — at least one required. */
  email?: string;
  phone?: string;
  password: string;
}

export interface RegisterCredentials {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
}

export interface OtpVerification {
  email: string;
  otpCode: string;
}

export interface ResendOtpRequest {
  email: string;
}

export interface GoogleAuthRequest {
  googleId: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    phoneNumber?: string;
    dateOfBirth?: string;
    isActive: boolean;
  };
}

export interface ApiResponse {
  code: number;
  message: string;
  data: any;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  phoneNumber?: string;
  dateOfBirth?: string;
  isActive: boolean;
}

/** Normalize roles from API / localStorage / JWT (string or string[]). */
const normalizeRoles = (roles: unknown): string[] => {
  if (!roles) return [];
  if (typeof roles === 'string') {
    const trimmed = roles.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(roles)) {
    return roles
      .map((r) => (r == null ? '' : String(r).trim()))
      .filter(Boolean);
  }
  return [];
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const rolesFromJwt = (token: string | null): string[] => {
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  if (!payload) return [];
  const roleClaim =
    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
    payload.role ??
    payload.roles;
  return normalizeRoles(roleClaim);
};

class AuthService {
  private tokenKey = 'authToken';
  private userKey = 'user';

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const email = credentials.email?.trim() || undefined;
      const phone = credentials.phone?.trim() || undefined;
      const payload = {
        password: credentials.password,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      };

      const response = await api.post('/auth/login', payload);
      const data = response.data;
      const token = data.token ?? data.Token;
      const rawUser = data.user ?? data.User;
      if (!token || !rawUser) {
        throw new Error('Login failed. Invalid server response.');
      }

      const roles = normalizeRoles(rawUser.roles ?? rawUser.Roles);
      const jwtRoles = rolesFromJwt(token);
      const user: User = {
        id: rawUser.id ?? rawUser.Id,
        email: rawUser.email ?? rawUser.Email ?? '',
        firstName: rawUser.firstName ?? rawUser.FirstName ?? '',
        lastName: rawUser.lastName ?? rawUser.LastName ?? '',
        roles: roles.length > 0 ? roles : jwtRoles,
        phoneNumber: rawUser.phoneNumber ?? rawUser.PhoneNumber,
        dateOfBirth: rawUser.dateOfBirth ?? rawUser.DateOfBirth,
        isActive: rawUser.isActive ?? rawUser.IsActive ?? true,
      };

      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userKey, JSON.stringify(user));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { token, user };
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.title ||
        (Array.isArray(data) ? data.join(', ') : null) ||
        'Login failed. Please check your credentials.';
      throw new Error(message);
    }
  }

  async register(credentials: RegisterCredentials): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/register', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  }

  async sendOtp(identifier: { email?: string; phone?: string }): Promise<ApiResponse> {
    try {
      const email = identifier.email?.trim() || undefined;
      const phone = identifier.phone?.trim() || undefined;
      // API requires phone for send-otp; email alone is not enough for phone-only accounts.
      const response = await api.post('/auth/send-otp', {
        ...(email ? { email } : {}),
        phone: phone || '',
      });
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      throw new Error(
        (typeof data === 'string' && data) ||
          data?.message ||
          'Failed to send OTP. Please try again.'
      );
    }
  }

  async verifyOtp(otpData: OtpVerification): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/verify-otp', otpData);
      const { token, user } = response.data.data;
      
      // Store token and user info
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userKey, JSON.stringify(user));
      
      // Set default auth header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'OTP verification failed. Please try again.');
    }
  }

  async resendOtp(email: string): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/resend-otp', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
    }
  }

  async googleAuth(googleData: GoogleAuthRequest): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/google-auth', googleData);
      const { token, user } = response.data.data;
      
      // Store token and user info
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userKey, JSON.stringify(user));
      
      // Set default auth header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Google authentication failed. Please try again.');
    }
  }

  async logout(): Promise<void> {
    try {
      // Call logout endpoint if available
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      // Clear local storage and headers
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
      delete api.defaults.headers.common['Authorization'];
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getRoles(): string[] {
    const stored = normalizeRoles(this.getUser()?.roles);
    if (stored.length > 0) return stored;
    return rolesFromJwt(this.getToken());
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    if (!userStr) return null;
    try {
      const raw = JSON.parse(userStr);
      const roles = normalizeRoles(raw.roles ?? raw.Roles);
      const jwtRoles = rolesFromJwt(this.getToken());
      return {
        ...raw,
        roles: roles.length > 0 ? roles : jwtRoles,
        email: raw.email ?? raw.Email ?? '',
        firstName: raw.firstName ?? raw.FirstName ?? '',
        lastName: raw.lastName ?? raw.LastName ?? '',
        id: raw.id ?? raw.Id,
        phoneNumber: raw.phoneNumber ?? raw.PhoneNumber,
        isActive: raw.isActive ?? raw.IsActive ?? true,
      } as User;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  hasRole(role: string): boolean {
    const wanted = role.trim().toLowerCase();
    if (!wanted) return false;
    return this.getRoles().some((r) => r.toLowerCase() === wanted);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  // Initialize auth state on app startup
  initializeAuth(): void {
    const token = this.getToken();
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
}

export const authService = new AuthService();
export default authService;
